import { attractionTypeCrudConfig, type AttractionType } from '@/config/objects/attraction-types';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface AttractionTypesListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<AttractionType>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (attractionType: AttractionType) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /attraction-types
   */
  baseUrl?: string;
}

/**
 * Reusable attraction types list component
 * Pass a custom config with modified actions to customize behavior
 */
export function AttractionTypesList({
  config,
  baseUrl = '/attraction-types',
  defaultWhere,
  onItemClick,
  className,
}: AttractionTypesListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (attractionType: AttractionType) => {
      if (onItemClick) {
        onItemClick(attractionType);
      } else {
        navigate(`/attraction-types/${attractionType.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || attractionTypeCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        getValueFromItem={(attractionType: AttractionType) => attractionType.id}
        getLabelFromItem={(attractionType: AttractionType) => attractionType.name}
      />
    </div>
  );
}
